import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useStatsigUser } from '@statsig/react-bindings';
import { datadogRum } from '@datadog/browser-rum';

import { fetchMember } from '../api/member';
import { Member } from '../interfaces/member';

export const useMember = (options?: Omit<UseQueryOptions<Member>, 'queryKey'>) => {
    const { updateUserAsync } = useStatsigUser();
    const { select, ...otherOptions } = options ?? {};

    const onSuccess = async (data: Member) => {
        // The RUM session is identified by auth user (`AuthProvider.fetchAuthenticationData`),
        // which is the only thing that runs for every session. Member id is carried
        // separately so it stays searchable — `/members/` returns the requester's own
        // record, so this can only ever be the session's own member.
        datadogRum.setUserProperty('member_id', data.id);

        // Merged onto the user Statsig holds when the fetch resolves; a copy captured at render time
        // could carry an identity that has since been replaced.
        await updateUserAsync((currentUser) => ({
            ...currentUser,
            custom: {
                ...currentUser.custom,
                firstPayment: data?.firstpayment ?? '',
                lastPayment: data?.lastpayment ?? '',
                userType: data?.usertype ?? '',
                incorporation: data?.incorporation ?? '',
                hasStaff: data?.business?.has_staff ?? false,
                // `member_stage` and `member_created` are snake_case unlike their neighbours because
                // Statsig matches the key string literally, and the backend sends them under exactly
                // these names (see `_member_custom_fields` in hyke/featureflags/statsig.py). Rename
                // either side and a gate targeting them evaluates on one side only.
                member_stage: data?.stage ?? '',
                // Epoch ms, as the backend sends it. Undefined (dropped when sent) rather than defaulted
                // when unknown, so a signup-date rule matches nobody instead of treating the member as
                // signed up at 0 — and a value merged from an earlier identity can't survive.
                member_created: data?.created ? Date.parse(data.created) : undefined,
            },
        }));
    };

    const { data, ...rest } = useQuery<Member>({
        queryKey: ['member'],
        queryFn: () => fetchMember(onSuccess),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        select: (member) => {
            const hasPaymentIssue = member?.status === 'payment_update' || member?.status === 'payment_error';
            const updatedMember = {
                ...member,
                hasPaymentIssue,
            };

            return select ? select(updatedMember) : updatedMember;
        },
        ...otherOptions,
    });

    return { data, ...rest };
};
